import React, { useCallback, useRef, useState } from 'react';
import './_capDocumentationBot.scss';
import { MetaDataBlock } from './UtilComponents';
import CapDivider from '../../CapDivider';
import CapHeading from '../../CapHeading';
import CapIcon from '../../CapIcon';
import CapRow from '../../CapRow';
import {
  clsPrefix,
  MESSAGE_CHUNK,
  MESSAGE_METADATA,
  MESSAGE_COMPLETE,
  MESSAGE_LIKE,
  MESSAGE_DISLIKE,
  MESSAGE_GET_ANSWER,
  SERVER_URL,
  DOCS_URL,
} from './constants';
import useWebsocket from '../hooks/useWebsocket';
import AiraContainer from '../UtilComponents/AiraContainer';
import LocaleHoc from '../../LocaleHoc';
import InputBox from '../UtilComponents/InputBox';
import { rootClsPrefix } from '../constants';


const DocumentationBot = ({
  textAskYourQuestion,
  textVisitDocs,
  textNeedHelp,
  textHowMayIHelp,
  textAira,
  textTooltip,
  textLinks,
  textConfidence,
  textWasHelpful,
  textCantConnect,
}) => {
  const queryId = useRef();
  const [query, setQuery] = useState('');
  const [lastQuery, setLastQuery] = useState();
  const [answerContent, setAnswerContent] = useState('');
  const [metaData, setMetaData] = useState();
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState();

  const onMessage = useCallback(msg => {
    if (msg.id !== queryId.current) return;
    if (msg.type === MESSAGE_CHUNK) {
      setAnswerContent(ans => ans + msg.chunk);
    }
    if (msg.type === MESSAGE_METADATA) {
      setMetaData(msg.metaData);
    }
    if (msg.type === MESSAGE_COMPLETE) {
      setLoading(false);
    }
    if (msg.type === MESSAGE_LIKE) {
      setRating(MESSAGE_LIKE);
    }
    if (msg.type === MESSAGE_DISLIKE) {
      setRating(MESSAGE_DISLIKE);
    }
  }, []);

  const onError = useCallback(() => {
    setAnswerContent(textCantConnect);
    setLoading(false);
  }, []);

  const { sendMessage, resetConnection } = useWebsocket(SERVER_URL, onMessage, onError);

  const onHide = () => {
    setQuery('');
    setLastQuery('');
    setAnswerContent('');
    setMetaData(null);
    setLoading(false);
    setRating(null);
    resetConnection();
  }

  // requests the server for answer
  const getAnswer = async () => {
    setLoading(true);
    setAnswerContent('');
    setRating(null);
    setMetaData(null);
    setLastQuery(query);
    const msgId = Date.now();
    queryId.current = msgId;
    sendMessage({
      id: msgId,
      type: MESSAGE_GET_ANSWER,
      query,
    });
  };

  // like or dislike the response
  const rateAnswer = async like => {
    sendMessage({
      id: queryId.current,
      type: like ? MESSAGE_LIKE : MESSAGE_DISLIKE,
    });
  };

  const likeAnswer = () => {
    rateAnswer(true);
  };

  const dislikeAnswer = () => {
    rateAnswer(false);
  };

  const onSubmit = () => {
    if (lastQuery === query) return;
    getAnswer();
  };

  return (
    <AiraContainer
      titlePadding="0.5rem"
      popupWidth="32rem"
      iconSize='2.2rem'
      title={textAira}
      titleDescription={textTooltip}
      onHide={onHide}
    >
      <div className={`${clsPrefix}-content`}>
        <CapHeading type="h3">{textHowMayIHelp}</CapHeading>
        <InputBox
          value={query}
          setValue={setQuery}
          showSend={query == lastQuery}
          loading={loading}
          placeholder={textAskYourQuestion}
          onSubmit={onSubmit}
        />
        <div
          className={`${clsPrefix}-answer-container ${rootClsPrefix}-no-scrollbar`}
        >
          {answerContent}

          {loading && <span className={`${clsPrefix}-cursor`}> </span>}

          {metaData &&
            !loading && (
              <MetaDataBlock
                metaData={metaData}
                likeAnswer={likeAnswer}
                dislikeAnswer={dislikeAnswer}
                rating={rating}
                textLinks={textLinks}
                textConfidence={textConfidence}
                textWasHelpful={textWasHelpful}
              />
            )}
        </div>
        <CapDivider className={`${clsPrefix}-divider`} />
        <CapRow type="flex" align="middle" className={`${clsPrefix}-more-help`}>
          <span>{textNeedHelp}</span>
          <a
            className={`${clsPrefix}-visit-text`}
            href={DOCS_URL}
            target="_blank"
          >
            {textVisitDocs}
            <CapIcon
              className={`${clsPrefix}-open-in-new-icon`}
              type="open-in-new-light"
              size="s"
            />
          </a>
        </CapRow>
      </div>
    </AiraContainer>
  );
};

export default LocaleHoc(DocumentationBot, {
  key: 'CapAskAira.DocumentationBot',
});
